export type TrackingLoadPhase = "downloading" | "initializing";

export type TrackingLoadProgress = {
  phase: TrackingLoadPhase;
  loadedBytes: number;
  totalBytes: number;
  percent: number;
};

export type TrackingAsset = {
  id: string;
  path: string;
  size: number;
  retain?: boolean;
};

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export function trackingWasmVariant(userAgent: string, simdSupported: boolean) {
  return simdSupported && !/Windows/i.test(userAgent) ? "simd" : "noSimd";
}

function joinChunks(chunks: Uint8Array[], size: number) {
  const result = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return result;
}

export async function downloadTrackingAssets(
  assets: TrackingAsset[],
  signal: AbortSignal,
  onProgress: (progress: TrackingLoadProgress) => void,
  fetcher: Fetcher = fetch,
) {
  const totalBytes = assets.reduce((total, asset) => total + asset.size, 0);
  let loadedBytes = 0;
  const report = () => onProgress({
    phase: "downloading",
    loadedBytes,
    totalBytes,
    percent: totalBytes > 0 ? Math.min(100, Math.floor((loadedBytes / totalBytes) * 100)) : 100,
  });

  report();
  const responses = await Promise.all(assets.map(async (asset) => {
    const response = await fetcher(asset.path, { cache: "force-cache", signal });
    if (!response.ok) throw new Error(`Tracking asset request failed: ${asset.path} (${response.status})`);
    return { asset, response };
  }));

  const retained = new Map<string, Uint8Array>();
  await Promise.all(responses.map(async ({ asset, response }) => {
    const chunks: Uint8Array[] = [];
    let assetBytes = 0;
    const reader = response.body?.getReader();
    if (!reader) {
      const bytes = new Uint8Array(await response.arrayBuffer());
      assetBytes = bytes.byteLength;
      loadedBytes += bytes.byteLength;
      if (asset.retain) chunks.push(bytes);
      report();
    } else {
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          assetBytes += value.byteLength;
          loadedBytes += value.byteLength;
          if (asset.retain) chunks.push(value);
          report();
        }
      } finally {
        reader.releaseLock();
      }
    }

    if (assetBytes !== asset.size) {
      throw new Error(`Tracking asset size mismatch: ${asset.path} (expected ${asset.size}, received ${assetBytes})`);
    }
    if (asset.retain) retained.set(asset.id, joinChunks(chunks, assetBytes));
  }));

  return retained;
}
